import { useMutation, useQuery } from "@tanstack/react-query";

import useAxios from "../common/hooks/useAxios";
import {
  ACTIVATE_USER,
  CHANGE_PASSWORD,
  CREATE_USER,
  DELETE_USER,
  GET_USER,
  LIST_USERS,
  LIST_USERS_ACCOUNTING,
  LIST_USERS_IMPORT,
  LIST_USERS_PRICING,
  RESET_PASSWORD,
  UPDATE_USER,
} from "../userManagement/api/users-api";
import { useCallback } from "react";

export const USER_QUERIES = {
  listUsers: "listUsers",
  listUsersAccounting: "listUsersAccounting",
  listUsersImport: "listUsersImport",
  listUsersPricing: "listUsersPricing",
  getUser: "getUser",
  createUser: "createUser",
  deleteUser: "deleteUser",
};

const useUserManagementApi = (enabledApis = []) => {
  const axios = useAxios();

  const {
    data: users = [],
    isFetching: isUsersLoading,
    refetch: listUsers,
  } = useQuery({
    queryKey: [USER_QUERIES.listUsers],
    queryFn: () => axios.get(LIST_USERS).then((res) => res.data),
    enabled: enabledApis.includes(USER_QUERIES.listUsers),
    staleTime: 5 * 60 * 1000,
  });
  const {
    data: usersAccounting = [],
    isFetching: isUsersAccountingLoading,
    refetch: listUsersAccounting,
  } = useQuery({
    queryKey: [USER_QUERIES.listUsersAccounting],
    queryFn: () => axios.get(LIST_USERS_ACCOUNTING).then((res) => res.data),
    enabled: enabledApis.includes(USER_QUERIES.listUsersAccounting),
  });
  const {
    data: usersImport = [],
    isFetching: isUsersImportLoading,
    refetch: listUsersImport,
  } = useQuery({
    queryKey: [USER_QUERIES.listUsersImport],
    queryFn: () => axios.get(LIST_USERS_IMPORT).then((res) => res.data),
    enabled: enabledApis.includes(USER_QUERIES.listUsersImport),
  });
  const {
    data: usersPricing = [],
    isFetching: isUsersPricingLoading,
    refetch: listUsersPricing,
  } = useQuery({
    queryKey: [USER_QUERIES.listUsersPricing],
    queryFn: () => axios.get(LIST_USERS_PRICING).then((res) => res.data),
    enabled: enabledApis.includes(USER_QUERIES.listUsersPricing),
  });

  const { mutateAsync: createUser, isPending: isCreateUserLoading } =
    useMutation({
      mutationFn: (data) =>
        axios.post(CREATE_USER, data).then((res) => res.data),
    });

  const { mutateAsync: updateUser, isPending: isUpdatingUser } = useMutation({
    mutationFn: (data) => axios.post(UPDATE_USER, data).then((res) => res.data),
  });

  const { mutateAsync: deleteUser, isPending: isDeleteUserLoading } =
    useMutation({
      mutationFn: (id) => axios.post(DELETE_USER(id)).then((res) => res.data),
    });

  const { mutateAsync: activateUser, isPending: isActivatingUser } =
    useMutation({
      mutationFn: (id) => axios.post(ACTIVATE_USER(id)).then((res) => res.data),
    });

  const { mutateAsync: changePassword, isPending: isChangingPassword } =
    useMutation({
      mutationFn: (data) =>
        axios.post(CHANGE_PASSWORD, data).then((res) => res.data),
    });

  const { mutateAsync: resetPassword, isPending: isResettingPassword } =
    useMutation({
      mutationFn: (data) =>
        axios.post(RESET_PASSWORD, data).then((res) => res.data),
    });

  const getUserById = useCallback(
    async (id) => {
      return await axios.get(GET_USER(id)).then((res) => res.data);
    },
    [axios]
  );

  return {
    listUsersApi: {
      execute: listUsers,
      isLoading: isUsersLoading,
      data: users,
    },
    listUsersAccountingApi: {
      execute: listUsersAccounting,
      isLoading: isUsersAccountingLoading,
      data: usersAccounting,
    },
    listUsersPricingApi: {
      execute: listUsersPricing,
      isLoading: isUsersPricingLoading,
      data: usersPricing,
    },
    listUsersImportApi: {
      execute: listUsersImport,
      isLoading: isUsersImportLoading,
      data: usersImport,
    },
    createUserApi: {
      create: createUser,
      isLoading: isCreateUserLoading,
    },
    updateUserApi: {
      execute: updateUser,
      isLoading: isUpdatingUser,
    },
    deleteUserApi: {
      delete: deleteUser,
      isLoading: isDeleteUserLoading,
    },
    activateUserApi: {
      execute: activateUser,
      isLoading: isActivatingUser,
    },
    getUserApi: {
      execute: getUserById,
    },
    changePasswordApi: {
      execute: changePassword,
      isLoading: isChangingPassword,
    },
    resetPasswordApi: {
      execute: resetPassword,
      isLoading: isResettingPassword,
    },
  };
};

export default useUserManagementApi;
